import React, { useState, useCallback } from 'react'
import toast from 'react-hot-toast'
import {
  regenerateSingleBoldLinkForOrder,
  regenerateBoldLinkForPartialRow
} from '../../../services/payments/boldRegenerateLink'

interface BoldRegenerateLinkButtonProps {
  orderId: number
  variant: 'main' | 'partial'
  rowIndex?: number
  /** Se invoca al hacer clic para usar totales/precios actuales del formulario */
  getTotalAmount: () => number
  /** Solo variant partial: abonos actuales del formulario para el PATCH */
  getPagosFromForm?: () => Array<Record<string, unknown>>
  storeName: string
  status: string
  paymentMethod?: string
  disabled?: boolean
  onRegenerated: (transactionId: string, url: string) => void
}

export function BoldRegenerateLinkButton({
  orderId,
  variant,
  rowIndex,
  getTotalAmount,
  getPagosFromForm,
  storeName,
  status,
  paymentMethod,
  disabled = false,
  onRegenerated
}: BoldRegenerateLinkButtonProps) {
  const [loading, setLoading] = useState(false)

  const handleClick = useCallback(async () => {
    if (disabled || loading) return
    const totalAmount = Math.round(getTotalAmount())
    if (totalAmount <= 0) {
      toast.error('El total del pedido debe ser mayor a 0 para generar el link Bold', {
        icon: '⚠️',
        position: 'top-center'
      })
      return
    }
    setLoading(true)
    try {
      const result =
        variant === 'main'
          ? await regenerateSingleBoldLinkForOrder({
              orderId,
              totalAmount,
              storeName,
              status,
              paymentMethod: paymentMethod || 'bold'
            })
          : await regenerateBoldLinkForPartialRow({
              orderId,
              rowIndex: rowIndex ?? 0,
              totalAmount,
              pagosFromForm: getPagosFromForm?.()
            })

      if (!result.ok) {
        toast.error(result.error || 'No se pudo generar el nuevo link Bold', {
          icon: '⚠️',
          position: 'top-center'
        })
        return
      }

      const txId = result.transactionId || ''
      const url = result.url || ''
      if (txId) {
        onRegenerated(txId, url)
      }
      toast.success('Nuevo link Bold generado', { icon: '✅', position: 'top-center' })
    } catch (err) {
      console.error('Error regenerando link Bold:', err)
      toast.error('Error al generar el nuevo link Bold', {
        icon: '⚠️',
        position: 'top-center'
      })
    } finally {
      setLoading(false)
    }
  }, [
    disabled,
    loading,
    variant,
    orderId,
    getTotalAmount,
    getPagosFromForm,
    storeName,
    status,
    paymentMethod,
    rowIndex,
    onRegenerated
  ])

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled || loading}
      className="px-2 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-xs disabled:cursor-not-allowed disabled:opacity-50"
      title={
        disabled
          ? 'No disponible: el pago ya está verificado'
          : 'Genera un nuevo link de pago Bold (el anterior dejará de usarse)'
      }
      aria-label="Generar nuevo link de pago Bold"
    >
      {loading ? 'Generando…' : 'Nuevo link'}
    </button>
  )
}
